import * as React from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Skeleton, SkeletonBox } from 'skullet/react'
import { ArrowLeft, FileText, Search } from 'lucide-react'

import { InvoiceSchema } from '@/gen/cortado/v1/invoices_pb'
import { EventLiteSchema } from '@/gen/cortado/v1/inquiries_pb'
import { EventStageEnum, InvoiceStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { formatDateTimeRange, formatTimestamp } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { useEventsList } from '@/stores/events.store'
import { useStore } from '@tanstack/react-store'
import { organizationStore } from '@/stores/organization.store'

import { InvoiceEditor } from '@/components/invoices/invoice-editor'
import { EventStageBadge } from '@/components/shared/enum-badge'
import { StripeAccountWarnings } from '@/components/shared/stripe-account-warnings'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'

export const Route = createFileRoute('/_app/_home/invoices/new')({
  component: NewInvoicePage,
})

const EVENT_SELECTOR_PAGE_SIZE = 100

const SAMPLE_BOOKINGS = [
  { contactFirstName: 'Jordan', contactLastName: 'Ellis', eventLocation: 'Harbor Pavilion, Portland' },
  { contactFirstName: 'Priya', contactLastName: 'Ramanathan', eventLocation: 'The Grand Hall' },
  { contactFirstName: 'Marcus', contactLastName: 'Okafor', eventLocation: 'Northfield Estate' },
  { contactFirstName: 'Lena', contactLastName: 'Hart', eventLocation: 'Riverside Gardens' },
  { contactFirstName: 'Sam', contactLastName: 'Whitaker', eventLocation: 'Union Station Loft' },
  { contactFirstName: 'Ana', contactLastName: 'Duarte', eventLocation: 'Cedar Barn' },
].map((booking, i) =>
  create(EventLiteSchema, {
    ...booking,
    id: `sample-${i}`,
    stage: EventStageEnum.EVENT_STAGE_UPCOMING,
    eventDate: timestampFromDate(new Date(2026, 6, 12 + i, 17)),
    eventLength: 4,
  }),
)

function NewInvoicePage() {
  const navigate = useNavigate()
  const breadcrumbs = React.useMemo(() => [
    { label: 'Home', href: '/home' },
    { label: 'Invoices', href: '/invoices' },
    { label: 'New Invoice' },
  ], [])

  useBreadcrumbFromRoute(breadcrumbs)

  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const [selectedEventId, setSelectedEventId] = React.useState<string>()
  const [search, setSearch] = React.useState('')
  const [eventLimit, setEventLimit] = React.useState(EVENT_SELECTOR_PAGE_SIZE)
  const { events, totalCount, isLoading, isFetching, hasLoaded } = useEventsList({ limit: eventLimit })

  const selectedEvent = events.find((event) => event.id === selectedEventId)
  const hasMoreEvents = events.length < totalCount

  const filteredEvents = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()
    if (!normalizedSearch) return events

    return events.filter((event) => {
      const name = formatFullName(event.contactFirstName, event.contactLastName, '').toLowerCase()
      const location = event.eventLocation?.toLowerCase() ?? ''
      return name.includes(normalizedSearch) || location.includes(normalizedSearch)
    })
  }, [events, search])

  const draftInvoice = React.useMemo(() => {
    if (!selectedEvent) return null

    return create(InvoiceSchema, {
      id: 'new-invoice',
      organizationId: selectedEvent.organizationId,
      bookingId: selectedEvent.bookingId,
      contactId: selectedEvent.contactId,
      status: InvoiceStatusEnum.INVOICE_STATUS_DRAFT,
      title: 'Event Services Invoice',
      lineItems: [],
    })
  }, [selectedEvent])

  const handleCreateSuccess = (invoiceId?: string) => {
    if (invoiceId) {
      navigate({ to: '/invoices/$id', params: { id: invoiceId } })
    } else {
      navigate({ to: '/invoices' })
    }
  }

  const handleLoadMoreEvents = () => {
    setEventLimit((currentLimit) => currentLimit + EVENT_SELECTOR_PAGE_SIZE)
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="display-heading text-2xl font-semibold tracking-tight">New Invoice</h1>
          <p className="text-sm text-muted-foreground">Select an existing booking before creating the invoice.</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/invoices">
            <ArrowLeft className="h-4 w-4" />
            Back to Invoices
          </Link>
        </Button>
      </div>

      {selectedOrganizationId && <StripeAccountWarnings organizationId={selectedOrganizationId} />}

      <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Select Booking</CardTitle>
            <CardDescription>Invoices must be tied to an existing booking and contact.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search bookings"
                className="pl-9"
              />
            </div>

            <Skeleton
              data={isLoading || !hasLoaded ? null : filteredEvents}
              sample={SAMPLE_BOOKINGS}
              id="invoices:new:bookings"
              label="Loading bookings"
              empty={hasLoaded && !isLoading && filteredEvents.length === 0}
              emptyMessage={
                <Empty className="border border-dashed py-12">
                  <EmptyMedia variant="icon">
                    <FileText className="h-5 w-5" />
                  </EmptyMedia>
                  <EmptyDescription>No bookings match this search.</EmptyDescription>
                  {hasMoreEvents && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleLoadMoreEvents}
                      disabled={isFetching}
                    >
                      Load more bookings
                    </Button>
                  )}
                </Empty>
              }
            >
              {(filteredEvents) => (
                <div className="space-y-3">
                  <div className="max-h-[640px] space-y-2 overflow-auto pr-1">
                    {filteredEvents.map((event) => {
                      const isSelected = selectedEventId === event.id
                      const name = formatFullName(event.contactFirstName, event.contactLastName, 'Unknown contact')

                      return (
                        <button
                          key={event.id}
                          type="button"
                          onClick={() => setSelectedEventId(event.id)}
                          className={`w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent/50 ${
                            isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'bg-card'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate font-medium">{name}</p>
                              <p className="truncate text-sm text-muted-foreground">
                                {event.eventLocation || 'No location set'}
                              </p>
                            </div>
                            <EventStageBadge stage={event.stage} />
                          </div>
                          <div className="mt-3 text-xs text-muted-foreground">
                            {event.eventDate
                              ? formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone)
                              : `Created ${formatTimestamp(event.createdAt)}`}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  {hasMoreEvents && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleLoadMoreEvents}
                      disabled={isFetching}
                    >
                      {isFetching ? 'Loading bookings...' : `Load more bookings (${events.length} of ${totalCount})`}
                    </Button>
                  )}
                </div>
              )}
            </Skeleton>
          </CardContent>
        </Card>

        <div className="min-w-0">
          {!selectedEventId ? (
            <Empty className="min-h-[420px] border border-dashed py-12">
              <EmptyMedia variant="icon">
                <FileText className="h-6 w-6" />
              </EmptyMedia>
              <EmptyDescription>Select a booking to open the invoice editor.</EmptyDescription>
            </Empty>
          ) : draftInvoice ? (
            <InvoiceEditor
              invoice={draftInvoice}
              organizationId={draftInvoice.organizationId}
              initialIsEditing={true}
              onCancel={() => navigate({ to: '/invoices' })}
              onSuccess={handleCreateSuccess}
            />
          ) : (
            <Skeleton loading label="Loading booking">
              <SkeletonBox width="100%" height={420} radius="0.5rem" />
            </Skeleton>
          )}
        </div>
      </div>
    </div>
  )
}
